/**
 * SearchBudgetEditor — Block S2.
 *
 * Inline editor for `params.search_budget` on a PresetTierModuleConfig.
 * Only renders for modules with `module_key === "search_enrichment"`.
 *
 * Server validates int/Decimal bounds in `validate_search_budget()`.
 * On 422 we surface the error message verbatim.
 */
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Save, Loader2, Info } from 'lucide-react';

import { useUpdateModuleConfig, type ModuleConfig } from '@/hooks/useAdmin';
import { InfoTip } from '@/components/admin/InfoTip';
import { cn } from '@/lib/utils';

interface SearchBudget {
  max_queries?: number;
  max_pages_per_row?: number;
  max_candidates_per_query?: number;
  unit_credit_per_query?: string;
  unit_credit_per_page_fetch?: string;
  unit_credit_per_extract?: string;
  notes?: string;
}

const EMPTY: SearchBudget = {
  max_queries: undefined,
  max_pages_per_row: undefined,
  max_candidates_per_query: undefined,
  unit_credit_per_query: '',
  unit_credit_per_page_fetch: '',
  unit_credit_per_extract: '',
  notes: '',
};

export function SearchBudgetEditor({ config }: { config: ModuleConfig }) {
  const { t } = useTranslation();
  const update = useUpdateModuleConfig();
  const initial = (config.params?.search_budget as SearchBudget | undefined) ?? {};
  const [draft, setDraft] = useState<SearchBudget>({ ...EMPTY, ...initial });
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // FIX 2026-09-02: setDraft vo vremya rendera vyzval 'Too many re-renders' (React #301).
  // Pereneseno v useEffect - sinhronizatsiya s serdevernymi dannymi tolko posle commit.
  useEffect(() => {
    if (
      !dirty &&
      JSON.stringify(initial) !== JSON.stringify(draft) &&
      !update.isPending
    ) {
      setDraft({ ...EMPTY, ...initial });
    }
  }, [dirty, initial, draft, update.isPending]);

  const setField = <K extends keyof SearchBudget>(k: K, v: SearchBudget[K]) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setDirty(true);
    setError(null);
  };

  const handleSave = () => {
    // Build payload — only include non-empty fields so partial edits work.
    const sb: Record<string, unknown> = {};
    if (draft.max_queries !== undefined && draft.max_queries !== null) {
      sb.max_queries = Number(draft.max_queries);
    }
    if (draft.max_pages_per_row !== undefined && draft.max_pages_per_row !== null) {
      sb.max_pages_per_row = Number(draft.max_pages_per_row);
    }
    if (draft.max_candidates_per_query !== undefined && draft.max_candidates_per_query !== null) {
      sb.max_candidates_per_query = Number(draft.max_candidates_per_query);
    }
    for (const k of [
      'unit_credit_per_query',
      'unit_credit_per_page_fetch',
      'unit_credit_per_extract',
    ] as const) {
      const v = draft[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        sb[k] = String(v).trim();
      }
    }
    if (draft.notes && draft.notes.trim()) sb.notes = draft.notes.trim();

    update.mutate(
      {
        id: config.id,
        body: { params: { search_budget: sb } },
      },
      {
        onSuccess: () => {
          setDirty(false);
          setError(null);
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            (err as { message?: string })?.message ??
            'Failed to save';
          setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        },
      },
    );
  };

  return (
    <div className="rounded-lg border border-primary-500/20 bg-primary-500/5 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Info className="w-4 h-4 text-primary-300" />
        <span className="text-sm font-semibold text-primary-200">
          {t('system.searchBudget.title', {
            defaultValue: 'Search budget override',
          })}
        </span>
        <span className="text-[10px] text-surface-500 ml-1 uppercase">
          {t('system.searchBudget.advisory', { defaultValue: 'Optional' })}
        </span>
      </div>

      <p className="text-xs text-surface-400">
        {t('system.searchBudget.help', {
          defaultValue:
            'Override the code defaults for this tier × search_enrichment. Empty fields fall back to code defaults.',
        })}
      </p>

      {/* Int bounds */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <IntField
          label={t('system.searchBudget.maxQueries', { defaultValue: 'Queries/row' })}
          hint="1–50"
          tip={t('system.searchBudget.tips.maxQueries', { defaultValue: 'Максимум поисковых запросов на строку результата' })}
          value={draft.max_queries ?? null}
          onChange={(v) => setField('max_queries', v ?? undefined)}
        />
        <IntField
          label={t('system.searchBudget.maxPagesPerRow', { defaultValue: 'Pages/row' })}
          hint="1–100"
          tip={t('system.searchBudget.tips.maxPagesPerRow', { defaultValue: 'Сколько страниц можно открыть на один поисковый запрос' })}
          value={draft.max_pages_per_row ?? null}
          onChange={(v) => setField('max_pages_per_row', v ?? undefined)}
        />
        <IntField
          label={t('system.searchBudget.maxCandidates', { defaultValue: 'Cands/query' })}
          hint="1–50"
          tip={t('system.searchBudget.tips.maxCandidates', { defaultValue: 'Сколько кандидатов выбирается на каждый запрос' })}
          value={draft.max_candidates_per_query ?? null}
          onChange={(v) => setField('max_candidates_per_query', v ?? undefined)}
        />
      </div>

      {/* Unit credits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <DecField
          label={t('system.searchBudget.creditPerQuery', { defaultValue: 'Credit / query' })}
          tip={t('system.searchBudget.tips.creditPerQuery', { defaultValue: 'Сколько кредитов списывается за один поисковый запрос' })}
          value={draft.unit_credit_per_query ?? ''}
          onChange={(v) => setField('unit_credit_per_query', v)}
        />
        <DecField
          label={t('system.searchBudget.creditPerPage', { defaultValue: 'Credit / page fetch' })}
          tip={t('system.searchBudget.tips.creditPerPage', { defaultValue: 'Сколько кредитов списывается за загрузку одной страницы' })}
          value={draft.unit_credit_per_page_fetch ?? ''}
          onChange={(v) => setField('unit_credit_per_page_fetch', v)}
        />
        <DecField
          label={t('system.searchBudget.creditPerExtract', { defaultValue: 'Credit / extract' })}
          tip={t('system.searchBudget.tips.creditPerExtract', { defaultValue: 'Сколько кредитов списывается за извлечение значения со страницы' })}
          value={draft.unit_credit_per_extract ?? ''}
          onChange={(v) => setField('unit_credit_per_extract', v)}
        />
      </div>

      <div>
        <label className="text-xs text-surface-500 uppercase tracking-wider">
          {t('system.searchBudget.notes', { defaultValue: 'Notes' })}
        </label>
        <input
          type="text"
          className="input w-full mt-1"
          value={draft.notes ?? ''}
          onChange={(e) => setField('notes', e.target.value)}
          placeholder="e.g. 'tight for big datasets'"
        />
      </div>

      {error && (
        <div className="text-xs text-danger bg-danger-tint/10 border border-danger-tint/20 rounded-md p-2">
          {error}
        </div>
      )}

      {dirty && (
        <button
          onClick={handleSave}
          disabled={update.isPending}
          className="btn-primary text-sm flex items-center gap-2"
        >
          {update.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {t('system.searchBudget.save', { defaultValue: 'Save search budget' })}
        </button>
      )}
    </div>
  );
}

function IntField({
  label,
  hint,
  tip,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  tip: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-1">
      <label className="text-xs text-surface-500 uppercase tracking-wider">
        {label}{' '}
        <span className="text-[10px] text-surface-500 normal-case">({hint})</span>
      </label>
      <InfoTip text={tip} />
      </div>
      <input
        type="number"
        className="input w-full mt-1 text-right"
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') onChange(null);
          else {
            const n = parseInt(v, 10);
            onChange(Number.isFinite(n) ? n : null);
          }
        }}
      />
    </div>
  );
}

function DecField({
  label,
  tip,
  value,
  onChange,
}: {
  label: string;
  tip: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-1">
      <label className="text-xs text-surface-500 uppercase tracking-wider">
        {label}
      </label>
      <InfoTip text={tip} />
      </div>
      <input
        type="text"
        inputMode="decimal"
        className={cn('input w-full mt-1 text-right font-mono')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0.05"
      />
    </div>
  );
}
